// T134 Artefact 1 — Open Graph + Twitter Card helper. Returns RR v7
// MetaDescriptor[]. Missing data degrades to sane defaults: `image: null`
// omits the image tags rather than emitting a broken reference.
import type { MetaDescriptor } from "react-router";
import type { Lang, RouteKey } from "~/config/route-config";
import {
  absoluteFromPath,
  absoluteUrl,
  OG_IMAGE,
  ogImageFor,
  OG_LOCALE,
  SITE_NAME,
  SITE_ORIGIN,
} from "./site";

export type OgType = "website" | "article" | "product" | "profile" | "book";

export interface OpenGraphInput {
  title: string;
  description: string;
  locale: Lang;
  type?: OgType;
  /**
   * Card image. `undefined` → the brand default for `locale` (T735: the claim is
   * burnt into the artwork, so ES and EN are different files); `null` → omit
   * image tags entirely; any string (root-relative or absolute) → used verbatim.
   */
  image?: string | null;
  /** Supply `routeKey` (+ `params`) to derive `og:url`… */
  routeKey?: RouteKey;
  params?: Record<string, string>;
  /** …or pass an explicit url/path (wins over `routeKey`). */
  url?: string;
}

/**
 * Composes `og:*` + `twitter:*` descriptors. `og:url` is derived from
 * `url` → `routeKey` → site origin, in that order of precedence.
 */
export function buildOpenGraph(input: OpenGraphInput): MetaDescriptor[] {
  const { title, description, locale, type = "website" } = input;

  const url = input.url
    ? absoluteFromPath(input.url)
    : input.routeKey
      ? absoluteUrl(input.routeKey, locale, input.params)
      : SITE_ORIGIN;

  const usingDefaultCard = input.image === undefined;
  const rawImage = usingDefaultCard ? ogImageFor(locale) : input.image;
  const image = rawImage ? absoluteFromPath(rawImage) : null;

  const tags: MetaDescriptor[] = [
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: type },
    { property: "og:url", content: url },
    { property: "og:locale", content: OG_LOCALE[locale] },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];

  if (image) {
    tags.push({ property: "og:image", content: image });
    tags.push({ name: "twitter:image", content: image });
    // T735 — declared only for the brand card, whose dimensions we know from
    // OG_IMAGE. A caller-supplied image (a guide cover) is any size, and stating
    // 1200×630 over it would be a lie that makes scrapers render a wrong box.
    if (usingDefaultCard) {
      tags.push({ property: "og:image:width", content: String(OG_IMAGE.width) });
      tags.push({ property: "og:image:height", content: String(OG_IMAGE.height) });
    }
  }

  return tags;
}
